import NextIcon from 'assets/icons/controls/NextIcon';
import PreviousIcon from 'assets/icons/controls/PreviousIcon';
import { IconSize } from 'assets/icons/icon-sizes';
import { BodyRegular, FlexCenter, FlexCol, FlexJustifyCenter, FlexSpaceBetween } from 'components/Elements';
import useWindowWidth from 'hooks/useWindowWidth';
import styled from 'styled-components';
import { sizes, theme } from 'theme';
import strings from '../../../../strings/strings.json';

type CourseNavigationProps = {
    showSkipButton?: boolean;
    previousButtonTitle: string;
    previousButtonAria: string;
    previousButtonAction: () => void;
    nextButtonTitle: string;
    nextButtonAria: string;
    nextButtonAction: () => void;
};

export function CourseNavigation({
    showSkipButton,
    previousButtonTitle,
    previousButtonAria,
    previousButtonAction,
    nextButtonTitle,
    nextButtonAria,
    nextButtonAction,
}: CourseNavigationProps): JSX.Element {
    const { isMobileWidth } = useWindowWidth();

    return (
        <CourseNavigationContainer>
            <Buttons>
                {!showSkipButton && (
                    <>
                        <PreviousButton onClick={previousButtonAction} aria-label={previousButtonAria}>
                            <IconWrapper>
                                <PreviousIcon size={IconSize.XLARGE} colour={theme.secondaryButtonTextColour} />
                            </IconWrapper>
                            <ButtonText>
                                <BodyRegular $bold>{strings.immersiveCourseViewButtons.backButton.text}</BodyRegular>
                                {!isMobileWidth && <BodyRegular $bold>{previousButtonTitle}</BodyRegular>}
                            </ButtonText>
                        </PreviousButton>
                        <NextButton onClick={nextButtonAction} aria-label={nextButtonAria}>
                            <ButtonText>
                                <BodyRegular $inverted $bold>
                                    {strings.immersiveCourseViewButtons.nextButton.text}
                                </BodyRegular>
                                {!isMobileWidth && (
                                    <BodyRegular $inverted $bold>
                                        {nextButtonTitle}
                                    </BodyRegular>
                                )}
                            </ButtonText>
                            <IconWrapper>
                                <NextIcon size={IconSize.XLARGE} colour={theme.primaryButtonTextColour} />
                            </IconWrapper>
                        </NextButton>
                    </>
                )}
                {showSkipButton && (
                    <SkipButton
                        onClick={nextButtonAction}
                        aria-label={strings.immersiveCourseViewButtons.skipQuestionnaireButton.aria}
                    >
                        <FlexCenter>
                            <BodyRegular $bold>
                                {strings.immersiveCourseViewButtons.skipQuestionnaireButton.text}
                            </BodyRegular>
                        </FlexCenter>
                    </SkipButton>
                )}
            </Buttons>
        </CourseNavigationContainer>
    );
}

const Button = styled.button`
    all: unset;
    display: flex;
    align-items: center;
    gap: ${sizes.spacingMd};
    padding: ${sizes.spacingSm} ${sizes.spacingMd};
    width: 100%;
    border-radius: ${sizes.borderRadiusLg};
    cursor: pointer;
`;

const IconWrapper = styled.div`
    min-width: 50px;
`;

const PreviousButton = styled(Button)`
    height: 5rem;
    background-color: ${theme.secondaryButtonBackgroundColour};
    border: 1px solid ${theme.secondaryButtonBorderColour};
    &:focus {
        background-color: ${theme.buttonFocusBackgroundColour};
    }
    &:hover {
        background-color: ${theme.secondaryButtonHoverBackgroundColour};
    }
`;

const NextButton = styled(Button)`
    height: 5rem;
    justify-content: end;
    background-color: ${theme.primaryButtonBackgroundColour};
    border: 1px solid ${theme.primaryButtonBorderColour};
    &:focus {
        background-color: ${theme.buttonFocusBackgroundColour};
    }
    &:focus p {
        color: ${theme.buttonFocusTextColour};
    }
    &:focus svg path {
        fill: ${theme.buttonFocusTextColour};
    }
    &:hover {
        background-color: ${theme.primaryButtonHoverBackgroundColour};
    }
    &:hover p {
        color: ${theme.primaryButtonTextColour};
    }
    &:hover svg path {
        fill: ${theme.primaryButtonTextColour};
    }
`;

const SkipButton = styled(Button)`
    height: 5rem;
    justify-content: center;
    background-color: ${theme.secondaryButtonBackgroundColour};
    border: 1px solid ${theme.secondaryButtonBackgroundColour};
    &:focus {
        background-color: ${theme.buttonFocusBackgroundColour};
    }
    &:hover {
        background-color: ${theme.secondaryButtonHoverBackgroundColour};
    }
`;

const ButtonText = styled(FlexCol)`
    max-height: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Buttons = styled(FlexSpaceBetween)`
    gap: ${sizes.spacingMd};
    margin: ${sizes.spacingLg} ${sizes.spacingMd};
    width: 900px;
`;

const CourseNavigationContainer = styled(FlexJustifyCenter)`
    width: 100%;
    background-color: ${theme.adminSubpageHeaderColour};
`;
